import { v4 as uuid } from 'uuid';

import {
  DprDetails,
  DprStatusTracker,
  DprTableOfColumns,
} from '@shared/models/dpr';

/**
 * DPRs are ordered by date and time rather than by calendar day, because a DPR
 * created on the device holds its date as UTC midnight, which west of UTC falls
 * on the calendar day before.
 */
export function getDprDate(dprDateTime: Date | string): number {
  return new Date(dprDateTime).valueOf();
}

/** By date, then by display id. */
export function compareDprOrder(a: DprDetails, b: DprDetails): number {
  return (
    getDprDate(a.dprDateTime) - getDprDate(b.dprDateTime) ||
    a.dprDisplayId - b.dprDisplayId
  );
}

/**
 * Whether the DPR comes directly before the earliest one in the list. Display ids
 * follow the date order, as the API renumbers the later DPRs when one is dated
 * between two, so that DPR's display id is one lower.
 */
export function isDprDirectlyBefore(
  dprDetails: DprDetails,
  dprDetailsList: DprDetails[]
): boolean {
  const earliestDpr = (dprDetailsList ?? []).reduce<DprDetails | null>(
    (earliest, dpr) =>
      !earliest || compareDprOrder(dpr, earliest) < 0 ? dpr : earliest,
    null
  );

  return (
    !!earliestDpr &&
    compareDprOrder(dprDetails, earliestDpr) < 0 &&
    dprDetails.dprDisplayId + 1 === earliestDpr.dprDisplayId
  );
}

/** The DPR dated last before the given date, if any. */
export function findPreviousDprDetails(
  dprDetailsList: DprDetails[],
  date: Date | string
): DprDetails | null {
  const beforeDate = getDprDate(date);

  return (dprDetailsList ?? []).reduce<DprDetails | null>((previous, dpr) => {
    const dprDate = getDprDate(dpr.dprDateTime);

    return dprDate < beforeDate &&
      (!previous || dprDate > getDprDate(previous.dprDateTime))
      ? dpr
      : previous;
  }, null);
}

export function createEmptyDprTableOfColumns(
  id: number | string,
  dprDetailId: number | string,
  tableOfColumnId: number
): DprTableOfColumns {
  return {
    id,
    comment: null,
    tableOfColumnId,
    dprDetailId,
    dprTableOfColumnsDetails: [],
    dprTableOfColumnsAttachment: [],
    syncDprTableOfColumnsDetails: null,
    syncDprTableOfColumnsAttachments: null,
  };
}

/**
 * The section gets an id of its own, so a Status Tracker save on it syncs as a
 * new section rather than as one without an id.
 */
export function createEmptyDprStatusTracker(
  dprDetailId: number | string
): DprStatusTracker {
  return {
    id: uuid(),
    dprDetailId,
    dprStatusTrackerDetails: [],
  };
}
